import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Switch } from "@/components/ui/switch";
import { AdminHeading } from "@/pages/admin/AdminShell";
import { LANGS } from "@/i18n/strings";

const inp = "mt-1 w-full border border-[#241914]/20 bg-white/60 px-3 py-2.5 text-sm outline-none focus:border-[#C6A76A]";

function Toggle({ label, desc, checked, onChange, testid }) {
  return (
    <label className="flex items-start justify-between gap-6 border-b border-[#241914]/10 py-5">
      <span><span className="block text-base">{label}</span>{desc && <span className="mt-1 block text-xs text-[#241914]/65">{desc}</span>}</span>
      <Switch dir="ltr" data-testid={testid} checked={!!checked} onCheckedChange={onChange} />
    </label>
  );
}

function PasswordForm() {
  const { t } = useLang();
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success(t("a_saved"));
      setCur("");
      setNw("");
    } catch (ex) {
      toast.error(errMsg(ex));
    }
  };
  return (
    <form onSubmit={submit} className="mt-14 max-w-xl" data-testid="admin-password-form">
      <p className="eyebrow !text-[#8a6d35]">{t("a_change_password")}</p>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label><span className="text-xs text-[#241914]/75">{t("a_current_password")}</span><input type="password" required className={inp} value={cur} onChange={(e) => setCur(e.target.value)} data-testid="admin-current-password" dir="ltr" /></label>
        <label><span className="text-xs text-[#241914]/75">{t("a_new_password")}</span><input type="password" required minLength={8} className={inp} value={nw} onChange={(e) => setNw(e.target.value)} data-testid="admin-new-password" dir="ltr" /></label>
      </div>
      <button className="mt-5 bg-[#0B0B0C] px-6 py-3 text-sm text-[#F7F3EB]" data-testid="admin-change-password-btn">{t("a_change_password")}</button>
    </form>
  );
}

export default function AdminSettings() {
  const { t } = useLang();
  const [s, setS] = useState(null);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setS({ ...r.data, address: { ar: "", fr: "", en: "", it: "", de: "", ...(r.data?.address || {}) } })).catch(() => setS({}));
  }, []);

  const save = async () => {
    try {
      const { data } = await api.put("/admin/settings", s);
      setS(data);
      toast.success(t("a_saved"));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!s) return null;
  const set = (k) => (v) => setS((x) => ({ ...x, [k]: v }));
  const setE = (k) => (e) => setS((x) => ({ ...x, [k]: e.target.value }));

  return (
    <div data-testid="admin-settings">
      <AdminHeading title={t("a_settings")}>
        <button onClick={save} data-testid="admin-settings-save-btn" className="bg-[#0B0B0C] px-8 py-3 text-sm text-[#F7F3EB] hover:bg-[#241914]">{t("a_save")}</button>
      </AdminHeading>
      <div className="grid grid-cols-1 gap-14 xl:grid-cols-2">
        <section>
          <p className="eyebrow !text-[#8a6d35]">{t("a_payment_methods")}</p>
          <Toggle testid="admin-settings-cod-switch" label={t("svc_cod_t")} checked={s.cod_enabled} onChange={set("cod_enabled")} />
          <Toggle testid="admin-settings-card-switch" label={t("svc_card_t")} desc={t("a_card_note")} checked={s.card_enabled} onChange={set("card_enabled")} />
          <Toggle testid="admin-settings-whatsapp-switch" label={t("svc_wa_t")} checked={s.whatsapp_enabled} onChange={set("whatsapp_enabled")} />
          <label className="mt-5 block">
            <span className="text-xs text-[#241914]/75">{t("a_wa_number")}</span>
            <input data-testid="admin-settings-whatsapp-input" className={inp} value={s.whatsapp_number || ""} onChange={setE("whatsapp_number")} dir="ltr" inputMode="tel" placeholder="212694356747" />
            <span className="mt-1 block text-xs text-[#241914]/60">{t("a_wa_contact_note")}</span>
          </label>
        </section>
        <section>
          <p className="eyebrow !text-[#8a6d35]">{t("a_contact")}</p>
          <div className="mt-4 space-y-4">
            <label className="block"><span className="text-xs text-[#241914]/75">{t("a_contact_phone")}</span><input data-testid="admin-settings-phone-input" className={inp} value={s.contact_phone || ""} onChange={setE("contact_phone")} dir="ltr" /></label>
            <label className="block"><span className="text-xs text-[#241914]/75">{t("a_contact_email")}</span><input data-testid="admin-settings-email-input" className={inp} value={s.contact_email || ""} onChange={setE("contact_email")} dir="ltr" /></label>
            <label className="block"><span className="text-xs text-[#241914]/75">{t("a_instagram")}</span><input data-testid="admin-settings-instagram-input" className={inp} value={s.instagram || ""} onChange={setE("instagram")} dir="ltr" placeholder="https://instagram.com/…" /></label>
            <fieldset className="border border-[#241914]/15 p-4" data-testid="admin-settings-address-group">
              <legend className="px-1 text-xs text-[#241914]/75">{t("a_address_hint")}</legend>
              {LANGS.map((l) => (
                <label key={l.code} className="mt-2 grid grid-cols-[5.5rem_1fr] items-center gap-3">
                  <span className="text-xs text-[#241914]/65">{l.label}</span>
                  <input data-testid={`admin-settings-address-${l.code}`} dir={l.dir} className={inp} value={s.address?.[l.code] || ""} onChange={(e) => setS((x) => ({ ...x, address: { ...x.address, [l.code]: e.target.value } }))} />
                </label>
              ))}
            </fieldset>
          </div>
        </section>
      </div>
      <PasswordForm />
    </div>
  );
}
